import React from 'react';
import { Chart } from 'react-chartjs-2';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import { Colors } from '~library/prerenderTheme';

import { numberFormatter } from '~library/numbers';
import { ColorsList, getXAxisConfig, percentageFormatter } from '../Chart';
import { crawlerCategoryLabel } from '~features/crawler-filter';
import { useIsPrinting } from '~library/print';

export const MAIN_CHART_ID = 'pages-crawled-main-chart';

type PagesCrawledMainChartProps = {
  intervals: string[];
  crawlersData: [string, number[]][];
  uncachedData: number[];
  granularity: AggregatedMetrics.IntervalGranularity;
};

function PagesCrawledMainChart({ intervals, crawlersData, uncachedData, granularity }: PagesCrawledMainChartProps) {
  const { isPrinting } = useIsPrinting();

  const uncachedDataset = React.useMemo(
    () => ({
      label: 'Cache Misses',
      data: uncachedData,
      backgroundColor: Colors.Orange,
      borderColor: Colors.Orange,
      type: 'line' as const,
      yAxisID: 'y-right',
      pointHitRadius: 25, // Large hover detection area
      order: 1,
    }),
    [uncachedData]
  );

  const crawlerDatasets = React.useMemo(
    () =>
      crawlersData.map(([crawler, data], i) => ({
        type: 'bar' as const,
        label: crawlerCategoryLabel(crawler),
        data,
        backgroundColor: ColorsList[i],
        borderColor: ColorsList[i],
        yAxisID: 'y-left',
        order: 2,
      })),
    [crawlersData]
  );

  return (
    <Chart
      id={MAIN_CHART_ID}
      type="bar"
      data={{
        labels: intervals,
        datasets: [...crawlerDatasets, uncachedDataset],
      }}
      options={{
        animation: isPrinting ? false : undefined,
        interaction: {
          intersect: false,
          mode: 'index',
          axis: 'x',
        },
        scales: {
          x: getXAxisConfig(granularity),
          'y-left': {
            type: 'linear',
            position: 'left',
            ticks: {
              count: 5,
              callback: (value) => numberFormatter.format(Number(value)),
            },
          },
          'y-right': {
            type: 'linear',
            position: 'right',
            min: 0,
            max: 100,
            ticks: {
              stepSize: 25,
              callback: (value) => value + '%',
            },
            grid: {
              drawOnChartArea: false, // avoids double gridlines
            },
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function (context) {
                // Custom label only for line dataset, first dataset
                if (context.datasetIndex === crawlersData.length) {
                  return `${context.dataset.label}: ${percentageFormatter(context.parsed.y ?? 0)}`;
                }
                // Default label for other datasets
                return `${context.dataset.label}: ${context.formattedValue}`;
              },
            },
          },
        },
      }}
    />
  );
}

export default PagesCrawledMainChart;
